import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../context/AuthContext";
import type { UserRole } from "../lib/api";
import { isAccessExpired } from "../lib/auth-tokens";

import { saveRedirectTarget } from "../lib/redirect";

import { StatusMessage } from "./ui/StatusMessage";

type Props = {
  children: ReactNode;
  role?: UserRole;
};

export function ProtectedRoute({ children, role }: Props) {
  const { isAuthenticated, role: userRole, tokens, refreshSession, logout, locked } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!tokens?.accessToken) {
        if (active) { setAllowed(false); setChecking(false); }
        return;
      }
      if (locked || !isAccessExpired(tokens)) {
        // Idle-locked admins keep their page; the lock screen asks for the password.
        if (active) { setAllowed(true); setChecking(false); }
        return;
      }
      const refreshed = await refreshSession();
      if (active) {
        setAllowed(Boolean(refreshed?.accessToken));
        setChecking(false);
        if (!refreshed) logout();
      }
    })();
    return () => { active = false; };
  }, [tokens, refreshSession, logout, locked]);

  if (checking) {
    return (
      <div className="w-full max-w-2xl mx-auto p-6 space-y-4 animate-pulse">
        <div className="h-8 bg-muted/60 rounded-md w-1/3" />
        <div className="h-4 bg-muted/60 rounded-md w-2/3" />
        <div className="h-36 bg-muted/60 rounded-md" />
      </div>
    );
  }

  if (!isAuthenticated || !allowed) {
    const fullTarget = location.pathname + location.search;
    if (fullTarget && fullTarget !== "/" && !fullTarget.startsWith("/auth")) {
      saveRedirectTarget(fullTarget);
      return <Navigate to={`/auth/login?redirect=${encodeURIComponent(fullTarget)}`} replace />;
    }
    logout();
    return <Navigate to="/" replace />;
  }

  if (role && userRole !== role) {
    // Admins may use the student app (quiz, notes, downloads) with full access.
    if (!(role === "student" && userRole === "admin")) {
      return <Navigate to={userRole === "admin" ? "/admin" : "/student"} replace />;
    }
  }

  return children;
}
